import { useState, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LinearInterpolator } from "react-map-gl";

import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import Select from "@mui/material/Select";
import { Typography } from "@mui/material";

import StyledTooltip from "../UI/StyledTooltip";
import { StyledButton } from "../UI/StyledButton";

import { aoiActions } from "../../store/index";
import { getData } from "../../store/dataSlice";

import classes from "./AOI.module.css";

import { ViewStateContext } from "../UrlParser/UrlParser";

import { aoiCategories } from "../../config/config.aoi";
import { styles } from "../../config/config.theme";

function AOI() {
  const dispatch = useDispatch();
  const aoiState = useSelector((state) => state.aoi);
  const dataState = useSelector((state) => state.data);
  const layerIds = useSelector((state) => Object.keys(state.layers));
  const { setViewState } = useContext(ViewStateContext);

  // Dropdown component requires {value, label} objects
  const aoiCatOptions = aoiCategories.map((aoiCat) => ({
    value: aoiCat.id,
    label: aoiCat.name,
    descriptionField: aoiCat.descriptionField ? aoiCat.descriptionField : null,
  }));

  const [selectedAoiCat, setSelectedAoiCat] = useState(
    aoiState.selection.category
      ? aoiState.selection.category
      : aoiCatOptions[0].value
  );

  const [aoiDescriptionField, setAoiDescriptionField] = useState(null);
  const aoiId = aoiCategories.find(
    (aoiCat) => aoiCat.id === selectedAoiCat
  ).data;

  const aoiOptions = !aoiId
    ? []
    : dataState[aoiId].data?.features?.map((feature) => {
        return {
          value: feature.id,
          label: aoiDescriptionField
            ? `${feature.properties.name} / ${feature.properties[aoiDescriptionField]}`
            : feature.properties.name,
        };
      });

  const [selectedAoiValues, setSelectedAoiValues] = useState(
    aoiState.selection.selectedIds.length > 0
      ? aoiOptions?.filter((option) =>
          aoiState.selection.selectedIds.includes(option.value)
        )
      : []
  );
  // update when a map selection is made
  useEffect(() => {
    setSelectedAoiValues(
      aoiOptions?.filter((option) =>
        aoiState.selection.selectedIds.includes(option.value)
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aoiState.selection.selectedIds]);

  function handleSelectOnMapClick() {
    dispatch(aoiActions.setOverlay({ id: selectedAoiCat }));
  }

  function handleAoiCatSelection(e) {
    const selectedOption = aoiCatOptions.find(
      (o) => o.value === e.target.value
    );
    setSelectedAoiCat(selectedOption.value);
    setAoiDescriptionField(selectedOption.descriptionField);
    setSelectedAoiValues([]);
    dispatch(
      aoiActions.setSelection({
        catId: null,
        selectedIds: [],
      })
    );
  }

  function handleAoiSelection(e) {
    const selectedOptions = aoiOptions.filter((option) => {
      return e.target.value.includes(option.value);
    });
    setSelectedAoiValues(selectedOptions);
    dispatch(
      aoiActions.setSelection({
        catId: selectedAoiCat,
        selectedIds: selectedOptions.map((o) => o.value),
      })
    );
  }

  function handleAoiOverlayClear() {
    dispatch(aoiActions.setOverlay({ id: null }));
  }

  function handleAoiClear() {
    setSelectedAoiValues([]);
    dispatch(
      aoiActions.setSelection({
        catId: null,
        selectedIds: [],
      })
    );
    setViewState((prevState) => {
      return {
        ...prevState,
        latitude: 37.09024,
        longitude: -95.712891,
        zoom: 3,
        transitionDuration: 700,
        transitionInterpolator: new LinearInterpolator(),
      };
    });
    layerIds.forEach((id) =>
      // filters data
      dispatch(getData({ dataId: id, source: "aoi" }))
    );
  }

  return (
    <div className={classes["aoi-content"]}>
      {
        <>
          <Box sx={{ display: "flex", alignItems: "center", m: 1 }}>
            <TravelExploreIcon color="white" sx={{ mr: 1 }} />
            <Typography variant="p">Area of Interest</Typography>
            <StyledTooltip title="Select multiple values using dropdown or by clicking SELECT ON MAP again">
              <InfoOutlinedIcon fontSize="small" sx={{ ml: 1 }} />
            </StyledTooltip>
          </Box>

          {/* Basin  */}
          {dataState[
            aoiCategories.find((aoiCat) => aoiCat.id === selectedAoiCat).data
          ].data && (
            <Box sx={{ p: 1, width: "100%", boxSizing: "border-box" }}>
              <FormControl fullWidth>
                <InputLabel
                  id="aoi-multiple-selection"
                  sx={{
                    color: styles["text-primary"],
                  }}
                >
                  Select Basin
                </InputLabel>
                {aoiOptions && (
                  <Select
                    labelId="aoi-multiple-selection"
                    id="aoi-multiple-selection"
                    multiple
                    value={selectedAoiValues.map((v) => v.value)}
                    onChange={handleAoiSelection}
                    input={<OutlinedInput label="Select Basin" />}
                    // MenuProps={MenuProps}
                    sx={{
                      color: styles["text-primary"],
                    }}
                  >
                    {aoiOptions.map((option) => {
                      return (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          sx={{
                            fontWeight: selectedAoiValues
                              .map((v) => v.value)
                              .includes(option.value)
                              ? "bold"
                              : "normal",
                          }}
                        >
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              </FormControl>
            </Box>
          )}
        </>
      }
      {!aoiState.overlay && (
        <Box sx={{ p: 1, width: "100%", boxSizing: "border-box" }}>
          <StyledButton
            fullWidth
            variant="outlined"
            className={classes["button"]}
            onClick={handleSelectOnMapClick}
          >
            Select on Map
          </StyledButton>
        </Box>
      )}
      {aoiState.overlay && (
        <Box sx={{ p: 1, width: "100%", boxSizing: "border-box" }}>
          <StyledButton
            fullWidth
            variant="outlined"
            className={classes["button"]}
            onClick={handleAoiOverlayClear}
          >
            Cancel Selection
          </StyledButton>
        </Box>
      )}
      {!aoiState.overlay &&
        aoiState.selection.category &&
        aoiState.selection.selectedIds.length > 0 &&
        dataState[
          aoiCategories.find((aoiCat) => aoiCat.id === selectedAoiCat).data
        ].data && (
          <Box sx={{ p: 1, width: "100%", boxSizing: "border-box" }}>
            <StyledButton
              fullWidth
              variant="contained"
              className={classes["button"]}
              onClick={handleAoiClear}
            >
              Clear Selection
            </StyledButton>
          </Box>
        )}
    </div>
  );
}

export default AOI;
